import { createContext, useContext, useState } from 'react'
import type { ReactNode } from 'react'

type Language = 'es' | 'en'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

// Traducciones
const translations = {
  es: {
    // Navbar
    'nav.home': 'Inicio',
    'nav.modules': 'Módulos',
    'nav.statistics': 'Estadísticas',
    'nav.management': 'Gestión',
    'nav.pricing': 'Precios',
    'nav.downloads': 'Descargas',
    'nav.login': 'Login',
    'nav.subtitle': 'Seguridad operativa',
    
    // Hero Section
    'hero.badge': 'Ciberseguridad + operaciones en tiempo real',
    'hero.title.prefix': 'Nexusdevtech:',
    'hero.title.main': 'El futuro de la seguridad operativa en tu mano',
    'hero.description': 'Una app móvil avanzada para gestión de personal, trazabilidad, SOS inmediato, tracking inteligente y comunicación táctica multiusuario. Minimalista, rápida y diseñada para entornos críticos.',
    'hero.feature.hardening': 'Hardening UX',
    'hero.feature.hardening.desc': 'Flujos críticos sin fricción.',
    'hero.feature.realtime': 'Tiempo real',
    'hero.feature.realtime.desc': 'Eventos y telemetría al instante.',
    'hero.feature.privacy': 'Privacidad',
    'hero.feature.privacy.desc': 'Principio de mínimo privilegio.',
    'hero.mobile.title': 'Nexusdevtech Mobile',
    'hero.mobile.soc': 'SOC-ready UI',
    'hero.mobile.sos': 'SOS',
    'hero.mobile.sos.desc': '10ms alert pipeline',
    'hero.mobile.tracking': 'Tracking',
    'hero.mobile.tracking.desc': 'Location Now + Register',
    'hero.mobile.ops': 'Ops',
    'hero.mobile.ops.desc': 'Checkpoints + VoIP',
    'hero.mobile.operator': 'Operador',
    'hero.mobile.active': 'Activo',
    'hero.mobile.unit': 'Unidad Alfa',
    'hero.mobile.traceability': 'Trazabilidad completa',
    'hero.mobile.session': 'Sesión segura',
    
    // Product Showcase
    'product.label': 'PRODUCTO',
    'product.title': 'Módulos clave, diseño tipo "Bento" y enfoque Zero‑Noise',
    'product.description': 'Cards con glassmorphism, jerarquía visual clara y microseñales de seguridad para operación rápida.',
    'product.user.title': 'User & History',
    'product.user.desc': 'Gestión de perfiles y trazabilidad completa.',
    'product.user.audit': 'Auditoría de cambios y eventos por usuario.',
    'product.user.roles': 'Roles operativos con principio de mínimo privilegio.',
    'product.user.visibility': 'Visibilidad total sin ruido: solo lo crítico.',
    'product.incidents.title': 'Incidents & SOS',
    'product.incidents.desc': 'Botón de pánico inmediato y reporte en tiempo real.',
    'product.incidents.latency': 'Latencia simulada',
    'product.incidents.notification': 'Notificación, geolocalización y contexto en un solo gesto.',
    'product.incidents.evidence': 'Evidencia temporal y adjuntos.',
    'product.incidents.escalation': 'Escalamiento inteligente a operación.',
    'product.tracking.title': 'Real-time Tracking',
    'product.tracking.desc': 'Location Now + Location Register (intervalo 5 min).',
    'product.tracking.location.now': 'Location Now',
    'product.tracking.location.now.precision': 'Precisión alta',
    'product.tracking.location.now.desc': 'Actualización continua en tiempo real para seguimiento inmediato.',
    'product.tracking.register': 'Location Register',
    'product.tracking.register.interval': 'Cada 5 min',
    'product.tracking.register.today': 'Registros hoy',
    'product.tracking.register.distance': 'Distancia total',
    'product.tracking.register.coverage': 'Cobertura',
    'product.tracking.register.recent': 'Últimos registros',
    'product.tracking.register.traceability': 'Trazabilidad completa:',
    'product.tracking.register.traceability.desc': 'Cada registro se almacena con timestamp, coordenadas y contexto. Permite auditoría, análisis de rutas y reconstrucción de eventos.',
    'product.tracking.zones.a': 'Zona A - Entrada Principal',
    'product.tracking.zones.b': 'Zona B - Estacionamiento',
    'product.tracking.zones.c': 'Zona C - Almacén',
    'product.tracking.zones.d': 'Zona D - Oficinas',
    'product.operations.title': 'Operations',
    'product.operations.desc': 'Checkpoints (rondas) y Walkie‑Talkie multiusuario (VoIP).',
    'product.operations.checkpoints': 'Checkpoints',
    'product.operations.checkpoints.title': 'Rondas verificables',
    'product.operations.checkpoints.desc': 'Confirmación de paso con evidencia temporal.',
    'product.operations.walkie': 'Walkie‑Talkie',
    'product.operations.walkie.title': 'Voz sobre IP',
    'product.operations.walkie.desc': 'Canales multiusuario y comunicación táctica.',
    'product.operations.patrols': 'Patrullas Programadas',
    'product.operations.patrols.title': 'Rutas automatizadas',
    'product.operations.patrols.desc': 'Configura horarios y zonas de patrullaje.',
    'product.operations.reports': 'Reportes',
    'product.operations.reports.title': 'Incidentes y eventos',
    'product.operations.reports.desc': 'Genera reportes detallados con evidencia.',
    'product.operations.alerts': 'Alertas Automáticas',
    'product.operations.alerts.title': 'Notificaciones push',
    'product.operations.alerts.desc': 'Alertas en tiempo real para eventos críticos.',
    'product.operations.dashboard': 'Dashboard',
    'product.operations.dashboard.title': 'Vista operativa',
    'product.operations.dashboard.desc': 'Monitoreo centralizado de todas las operaciones.',
    
    // Live Stats
    'stats.label': 'ESTADÍSTICAS',
    'stats.title': 'Señales de confianza. Métricas instantáneas.',
    'stats.description': 'Contadores animados para simular performance de seguridad. (Datos de demostración)',
    'stats.uptime': 'Uptime',
    'stats.uptime.desc': 'Disponibilidad operativa (simulada).',
    'stats.sos': 'Latencia SOS',
    'stats.sos.desc': 'Disparo + encolado de alerta (simulado).',
    'stats.register': 'Registro',
    'stats.register.desc': 'Intervalo de tracking programado.',
    'stats.events': 'Eventos/min',
    'stats.events.desc': 'Telemetría agregada (simulada).',
    
    // Security Management
    'security.label': 'GESTIÓN DE SEGURIDAD',
    'security.title': 'Control total de tu operación',
    'security.description': 'Visualiza en tiempo real la ubicación de tus guardias, gestiona patrullas y mantén el control total de tu empresa de seguridad desde un solo lugar.',
    'security.guards.active': 'Guardias Activos',
    'security.guards.patrol': 'En Patrulla',
    'security.checkpoints': 'Checkpoints Hoy',
    'security.response': 'Tiempo Promedio Respuesta',
    'security.activity.title': 'Actividad Reciente',
    'security.activity.desc': 'Timeline de acciones y eventos en tiempo real.',
    'security.battery.title': 'Estado de Baterías',
    'security.battery.desc': 'Monitoreo de energía de dispositivos móviles.',
    'security.guard.alpha': 'Guardia Alpha',
    'security.guard.beta': 'Guardia Beta',
    'security.guard.gamma': 'Guardia Gamma',
    'security.guard.delta': 'Guardia Delta',
    'security.action.checkpoint': 'Checkpoint completado',
    'security.action.patrol': 'Inició patrulla Zona A',
    'security.action.incident': 'Reporte de incidente menor',
    'security.time.ago': 'Hace',
    'security.time.min': 'min',
    
    // Pricing
    'pricing.label': 'PRECIOS',
    'pricing.title': 'Planes flexibles para cada operación',
    'pricing.description': 'Elige el plan que mejor se adapte a tu empresa de seguridad. Todos incluyen las funcionalidades esenciales.',
    'pricing.standard': 'Estándar',
    'pricing.standard.desc': 'Perfecto para equipos pequeños',
    'pricing.pro': 'Pro',
    'pricing.pro.desc': 'Ideal para operaciones medianas',
    'pricing.promax': 'Pro Max',
    'pricing.promax.desc': 'Solución empresarial completa',
    'pricing.month': '/mes',
    'pricing.per.user': 'por usuario',
    'pricing.contact': 'Contactar',
    'pricing.contact.desc': 'con el proveedor',
    'pricing.users': 'usuarios',
    'pricing.unlimited': 'Usuarios ilimitados',
    'pricing.includes': 'Incluye:',
    'pricing.cta.start': 'Comenzar ahora',
    'pricing.cta.contact': 'Contactar proveedor',
    'pricing.feature.users': 'Crear usuarios',
    'pricing.popular': 'Más Popular',
    'pricing.feature.history': 'History',
    'pricing.feature.incidents': 'Incidents',
    'pricing.feature.locations': 'Locations',
    'pricing.feature.sos': 'SOS',
    'pricing.feature.checkpoint': 'Checkpoint',
    'pricing.feature.walkie': 'Walkie-Talkie',
    'pricing.footer': 'Todos los planes incluyen soporte técnico y actualizaciones de seguridad.',
    'pricing.custom': '¿Necesitas un plan personalizado?',
    'pricing.custom.link': 'Contáctanos',
    
    // Downloads
    'downloads.title': 'Descarga la App',
    'downloads.description': 'Botones listos para enlazar a tus URLs reales de App Store / Play Store.',
    'downloads.appstore': 'Descargar en',
    'downloads.playstore': 'Disponible en',
    
    // Footer
    'footer.tagline': 'Diseñamos experiencias seguras para operaciones críticas. Minimalismo, velocidad y control.',
    'footer.legal': 'LEGAL',
    'footer.privacy': 'Política de Privacidad',
    'footer.terms': 'Términos',
    'footer.security': 'Seguridad',
    'footer.contact': 'Contacto',
    'footer.social': 'REDES',
    'footer.status': 'ESTADO',
    'footer.services': 'Servicios',
    'footer.operational': 'Operativos',
    'footer.uptime': 'Uptime y métricas: demostración visual.',
    'footer.copyright': 'Todos los derechos reservados.',
    'footer.maps': 'Mapas:',
    'footer.theme': 'Deep Black • Electric Blue • Minimal Secure UI',
    
    // Legal Pages
    'legal.privacy.label': 'LEGAL',
    'legal.privacy.title': 'Política de Privacidad',
    'legal.privacy.lastUpdated': 'Última actualización: Enero 2026',
    'legal.privacy.section1.title': '1. Recopilación de Información',
    'legal.privacy.section1.content': 'Nexusdevtech recopila información necesaria para proporcionar nuestros servicios de seguridad operativa. Esto incluye datos de usuario, información de ubicación, registros de actividad y datos de comunicación.\n\nLa información se recopila de forma transparente y solo para los fines operativos necesarios para el funcionamiento de la plataforma.',
    'legal.privacy.section2.title': '2. Uso de la Información',
    'legal.privacy.section2.content': 'Utilizamos la información recopilada exclusivamente para:\n\n• Proporcionar y mejorar nuestros servicios de seguridad\n• Gestionar operaciones y patrullas\n• Responder a incidentes y alertas SOS\n• Mantener la trazabilidad y auditoría requerida\n• Cumplir con obligaciones legales y regulatorias',
    'legal.privacy.section3.title': '3. Protección de Datos',
    'legal.privacy.section3.content': 'Implementamos medidas de seguridad técnicas y organizativas avanzadas para proteger sus datos:\n\n• Cifrado end-to-end para comunicaciones sensibles\n• Autenticación multifactor\n• Monitoreo continuo de seguridad\n• Acceso basado en roles con principio de mínimo privilegio\n• Respaldo y recuperación de datos',
    'legal.privacy.section4.title': '4. Compartir Información',
    'legal.privacy.section4.content': 'No vendemos ni compartimos su información personal con terceros, excepto cuando sea necesario para:\n\n• Cumplir con requisitos legales\n• Responder a órdenes judiciales\n• Proteger nuestros derechos legales\n• En caso de transferencia de negocio (con notificación previa)',
    'legal.privacy.section5.title': '5. Sus Derechos',
    'legal.privacy.section5.content': 'Usted tiene derecho a:\n\n• Acceder a sus datos personales\n• Rectificar información incorrecta\n• Solicitar la eliminación de datos\n• Oponerse al procesamiento de datos\n• Portabilidad de datos\n• Presentar una queja ante la autoridad de protección de datos',
    
    'legal.terms.label': 'LEGAL',
    'legal.terms.title': 'Términos y Condiciones',
    'legal.terms.lastUpdated': 'Última actualización: Enero 2026',
    'legal.terms.section1.title': '1. Aceptación de los Términos',
    'legal.terms.section1.content': 'Al acceder y utilizar los servicios de Nexusdevtech, usted acepta estar sujeto a estos Términos y Condiciones.\n\nSi no está de acuerdo con alguna parte de estos términos, no debe utilizar nuestros servicios.',
    'legal.terms.section2.title': '2. Descripción del Servicio',
    'legal.terms.section2.content': 'Nexusdevtech proporciona una plataforma de gestión de seguridad operativa que incluye:\n\n• Seguimiento en tiempo real de personal\n• Gestión de incidentes y alertas SOS\n• Comunicación táctica multiusuario\n• Reportes y auditoría\n• Integración con sistemas de seguridad existentes',
    'legal.terms.section3.title': '3. Uso Aceptable',
    'legal.terms.section3.content': 'Usted se compromete a utilizar nuestros servicios únicamente para fines legítimos de seguridad operativa.\n\nEstá prohibido:\n\n• Usar el servicio para actividades ilegales\n• Intentar acceder no autorizado a sistemas\n• Interferir con el funcionamiento del servicio\n• Compartir credenciales de acceso\n• Realizar ingeniería inversa del software',
    'legal.terms.section4.title': '4. Limitación de Responsabilidad',
    'legal.terms.section4.content': 'Nexusdevtech no será responsable de daños indirectos, incidentales o consecuentes derivados del uso de nuestros servicios.\n\nNuestra responsabilidad se limita al monto pagado por el servicio en los últimos 12 meses.',
    'legal.terms.section5.title': '5. Modificaciones',
    'legal.terms.section5.content': 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor al publicarse en nuestro sitio web.\n\nEl uso continuado del servicio después de las modificaciones constituye su aceptación de los nuevos términos.',
    
    'legal.security.label': 'SEGURIDAD',
    'legal.security.title': 'Seguridad y Protección de Datos',
    'legal.security.description': 'En Nexusdevtech, la seguridad es fundamental. Implementamos múltiples capas de protección para garantizar la integridad y confidencialidad de sus datos operativos.',
    'legal.security.encryption.title': 'Cifrado de Datos',
    'legal.security.encryption.desc': 'Todos los datos se cifran en tránsito (TLS 1.3) y en reposo (AES-256). Las comunicaciones sensibles utilizan cifrado end-to-end.',
    'legal.security.auth.title': 'Autenticación',
    'legal.security.auth.desc': 'Autenticación multifactor (MFA) obligatoria, gestión de sesiones seguras y tokens de acceso con expiración automática.',
    'legal.security.monitoring.title': 'Monitoreo Continuo',
    'legal.security.monitoring.desc': 'Sistema de detección de intrusiones (IDS), análisis de comportamiento y alertas automáticas ante actividades sospechosas.',
    'legal.security.infrastructure.title': 'Infraestructura Segura',
    'legal.security.infrastructure.desc': 'Servidores en centros de datos certificados, backups automáticos diarios y planes de recuperación ante desastres.',
    'legal.security.commitment.title': 'Nuestro Compromiso',
    'legal.security.commitment.content': 'Mantenemos los más altos estándares de seguridad en la industria.\n\nRealizamos auditorías de seguridad regulares, pruebas de penetración y cumplimos con normativas internacionales de protección de datos.\n\nSi descubre una vulnerabilidad de seguridad, por favor contáctenos de forma responsable a través de nuestro programa de divulgación coordinada.',
    
    // Contact Page
    'contact.label': 'CONTACTO',
    'contact.title': 'Contáctanos',
    'contact.description': '¿Tienes preguntas sobre nuestros servicios? Estamos aquí para ayudarte. Completa el formulario y nos pondremos en contacto contigo lo antes posible.',
    'contact.form.name': 'Nombre',
    'contact.form.name.placeholder': 'Tu nombre completo',
    'contact.form.email': 'Correo electrónico',
    'contact.form.email.placeholder': 'tu@email.com',
    'contact.form.company': 'Empresa',
    'contact.form.company.placeholder': 'Nombre de tu empresa (opcional)',
    'contact.form.message': 'Mensaje',
    'contact.form.message.placeholder': 'Cuéntanos cómo podemos ayudarte...',
    'contact.form.submit': 'Enviar mensaje',
    'contact.form.submitting': 'Enviando...',
    'contact.success.title': '¡Mensaje enviado!',
    'contact.success.message': 'Gracias por contactarnos. Te responderemos en breve.',
    'contact.info.email.title': 'Email',
    'contact.info.response.title': 'Tiempo de respuesta',
    'contact.info.response.desc': 'Respondemos en un plazo de 24-48 horas hábiles.',
  },
  en: {
    // Navbar
    'nav.home': 'Home',
    'nav.modules': 'Modules',
    'nav.statistics': 'Statistics',
    'nav.management': 'Management',
    'nav.pricing': 'Pricing',
    'nav.downloads': 'Downloads',
    'nav.login': 'Login',
    'nav.subtitle': 'Operational security',
    
    // Hero Section
    'hero.badge': 'Cybersecurity + real-time operations',
    'hero.title.prefix': 'Nexusdevtech:',
    'hero.title.main': 'The future of operational security in your hand',
    'hero.description': 'An advanced mobile app for personnel management, traceability, immediate SOS, intelligent tracking and multi-user tactical communication. Minimalist, fast and designed for critical environments.',
    'hero.feature.hardening': 'Hardening UX',
    'hero.feature.hardening.desc': 'Critical flows without friction.',
    'hero.feature.realtime': 'Real-time',
    'hero.feature.realtime.desc': 'Events and telemetry instantly.',
    'hero.feature.privacy': 'Privacy',
    'hero.feature.privacy.desc': 'Principle of least privilege.',
    'hero.mobile.title': 'Nexusdevtech Mobile',
    'hero.mobile.soc': 'SOC-ready UI',
    'hero.mobile.sos': 'SOS',
    'hero.mobile.sos.desc': '10ms alert pipeline',
    'hero.mobile.tracking': 'Tracking',
    'hero.mobile.tracking.desc': 'Location Now + Register',
    'hero.mobile.ops': 'Ops',
    'hero.mobile.ops.desc': 'Checkpoints + VoIP',
    'hero.mobile.operator': 'Operator',
    'hero.mobile.active': 'Active',
    'hero.mobile.unit': 'Alpha Unit',
    'hero.mobile.traceability': 'Full traceability',
    'hero.mobile.session': 'Secure session',
    
    // Product Showcase
    'product.label': 'PRODUCT',
    'product.title': 'Key modules, "Bento" style design and Zero‑Noise approach',
    'product.description': 'Cards with glassmorphism, clear visual hierarchy and security micro-signals for fast operation.',
    'product.user.title': 'User & History',
    'product.user.desc': 'Profile management and complete traceability.',
    'product.user.audit': 'Audit of changes and events per user.',
    'product.user.roles': 'Operational roles with principle of least privilege.',
    'product.user.visibility': 'Total visibility without noise: only critical.',
    'product.incidents.title': 'Incidents & SOS',
    'product.incidents.desc': 'Immediate panic button and real-time reporting.',
    'product.incidents.latency': 'Simulated latency',
    'product.incidents.notification': 'Notification, geolocation and context in one gesture.',
    'product.incidents.evidence': 'Temporal evidence and attachments.',
    'product.incidents.escalation': 'Intelligent escalation to operation.',
    'product.tracking.title': 'Real-time Tracking',
    'product.tracking.desc': 'Location Now + Location Register (5 min interval).',
    'product.tracking.location.now': 'Location Now',
    'product.tracking.location.now.precision': 'High precision',
    'product.tracking.location.now.desc': 'Continuous real-time update for immediate tracking.',
    'product.tracking.register': 'Location Register',
    'product.tracking.register.interval': 'Every 5 min',
    'product.tracking.register.today': 'Records today',
    'product.tracking.register.distance': 'Total distance',
    'product.tracking.register.coverage': 'Coverage',
    'product.tracking.register.recent': 'Latest records',
    'product.tracking.register.traceability': 'Complete traceability:',
    'product.tracking.register.traceability.desc': 'Each record is stored with timestamp, coordinates and context. Allows auditing, route analysis and event reconstruction.',
    'product.tracking.zones.a': 'Zone A - Main Entrance',
    'product.tracking.zones.b': 'Zone B - Parking',
    'product.tracking.zones.c': 'Zone C - Warehouse',
    'product.tracking.zones.d': 'Zone D - Offices',
    'product.operations.title': 'Operations',
    'product.operations.desc': 'Checkpoints (rounds) and multi-user Walkie‑Talkie (VoIP).',
    'product.operations.checkpoints': 'Checkpoints',
    'product.operations.checkpoints.title': 'Verifiable rounds',
    'product.operations.checkpoints.desc': 'Pass confirmation with temporal evidence.',
    'product.operations.walkie': 'Walkie‑Talkie',
    'product.operations.walkie.title': 'Voice over IP',
    'product.operations.walkie.desc': 'Multi-user channels and tactical communication.',
    'product.operations.patrols': 'Scheduled Patrols',
    'product.operations.patrols.title': 'Automated routes',
    'product.operations.patrols.desc': 'Configure schedules and patrol zones.',
    'product.operations.reports': 'Reports',
    'product.operations.reports.title': 'Incidents and events',
    'product.operations.reports.desc': 'Generates detailed reports with evidence.',
    'product.operations.alerts': 'Automatic Alerts',
    'product.operations.alerts.title': 'Push notifications',
    'product.operations.alerts.desc': 'Real-time alerts for critical events.',
    'product.operations.dashboard': 'Dashboard',
    'product.operations.dashboard.title': 'Operational view',
    'product.operations.dashboard.desc': 'Centralized monitoring of all operations.',
    
    // Live Stats
    'stats.label': 'STATISTICS',
    'stats.title': 'Trust signals. Instant metrics.',
    'stats.description': 'Animated counters to simulate security performance. (Demo data)',
    'stats.uptime': 'Uptime',
    'stats.uptime.desc': 'Operational availability (simulated).',
    'stats.sos': 'SOS Latency',
    'stats.sos.desc': 'Alert trigger + queuing (simulated).',
    'stats.register': 'Register',
    'stats.register.desc': 'Scheduled tracking interval.',
    'stats.events': 'Events/min',
    'stats.events.desc': 'Aggregated telemetry (simulated).',
    
    // Security Management
    'security.label': 'SECURITY MANAGEMENT',
    'security.title': 'Total control of your operation',
    'security.description': 'View in real-time the location of your guards, manage patrols and maintain total control of your security company from one place.',
    'security.guards.active': 'Active Guards',
    'security.guards.patrol': 'On Patrol',
    'security.checkpoints': 'Checkpoints Today',
    'security.response': 'Average Response Time',
    'security.activity.title': 'Recent Activity',
    'security.activity.desc': 'Timeline of actions and events in real-time.',
    'security.battery.title': 'Battery Status',
    'security.battery.desc': 'Monitoring of mobile device power.',
    'security.guard.alpha': 'Guard Alpha',
    'security.guard.beta': 'Guard Beta',
    'security.guard.gamma': 'Guard Gamma',
    'security.guard.delta': 'Guard Delta',
    'security.action.checkpoint': 'Checkpoint completed',
    'security.action.patrol': 'Started patrol Zone A',
    'security.action.incident': 'Minor incident report',
    'security.time.ago': 'Ago',
    'security.time.min': 'min',
    
    // Pricing
    'pricing.label': 'PRICING',
    'pricing.title': 'Flexible plans for every operation',
    'pricing.description': 'Choose the plan that best fits your security company. All include essential features.',
    'pricing.standard': 'Standard',
    'pricing.standard.desc': 'Perfect for small teams',
    'pricing.pro': 'Pro',
    'pricing.pro.desc': 'Ideal for medium operations',
    'pricing.promax': 'Pro Max',
    'pricing.promax.desc': 'Complete enterprise solution',
    'pricing.month': '/month',
    'pricing.per.user': 'per user',
    'pricing.contact': 'Contact',
    'pricing.contact.desc': 'provider',
    'pricing.users': 'users',
    'pricing.unlimited': 'Unlimited users',
    'pricing.includes': 'Includes:',
    'pricing.cta.start': 'Get started',
    'pricing.cta.contact': 'Contact provider',
    'pricing.feature.users': 'Create users',
    'pricing.popular': 'Most Popular',
    'pricing.feature.history': 'History',
    'pricing.feature.incidents': 'Incidents',
    'pricing.feature.locations': 'Locations',
    'pricing.feature.sos': 'SOS',
    'pricing.feature.checkpoint': 'Checkpoint',
    'pricing.feature.walkie': 'Walkie-Talkie',
    'pricing.footer': 'All plans include technical support and security updates.',
    'pricing.custom': 'Need a custom plan?',
    'pricing.custom.link': 'Contact us',
    
    // Footer
    'footer.tagline': 'We design secure experiences for critical operations. Minimalism, speed, and control.',
    'footer.legal': 'LEGAL',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms',
    'footer.security': 'Security',
    'footer.contact': 'Contact',
    'footer.social': 'NETWORKS',
    'footer.status': 'STATUS',
    'footer.services': 'Services',
    'footer.operational': 'Operational',
    'footer.uptime': 'Uptime and metrics: visual demonstration.',
    'footer.copyright': 'All rights reserved.',
    'footer.maps': 'Maps:',
    'footer.theme': 'Deep Black • Electric Blue • Minimal Secure UI',
    
    // Downloads
    'downloads.title': 'Download the App',
    'downloads.description': 'Buttons ready to link to your real App Store / Play Store URLs.',
    'downloads.appstore': 'Download on',
    'downloads.playstore': 'Available on',
    
    // Legal Pages
    'legal.privacy.label': 'LEGAL',
    'legal.privacy.title': 'Privacy Policy',
    'legal.privacy.lastUpdated': 'Last updated: January 2026',
    'legal.privacy.section1.title': '1. Information Collection',
    'legal.privacy.section1.content': 'Nexusdevtech collects information necessary to provide our operational security services. This includes user data, location information, activity logs, and communication data.\n\nThe information is collected transparently and only for operational purposes necessary for the platform to function.',
    'legal.privacy.section2.title': '2. Use of Information',
    'legal.privacy.section2.content': 'We use the collected information exclusively for:\n\n• Providing and improving our security services\n• Managing operations and patrols\n• Responding to incidents and SOS alerts\n• Maintaining required traceability and auditing\n• Complying with legal and regulatory obligations',
    'legal.privacy.section3.title': '3. Data Protection',
    'legal.privacy.section3.content': 'We implement advanced technical and organizational security measures to protect your data:\n\n• End-to-end encryption for sensitive communications\n• Multi-factor authentication\n• Continuous security monitoring\n• Role-based access with principle of least privilege\n• Data backup and recovery',
    'legal.privacy.section4.title': '4. Information Sharing',
    'legal.privacy.section4.content': 'We do not sell or share your personal information with third parties, except when necessary for:\n\n• Complying with legal requirements\n• Responding to court orders\n• Protecting our legal rights\n• In case of business transfer (with prior notice)',
    'legal.privacy.section5.title': '5. Your Rights',
    'legal.privacy.section5.content': 'You have the right to:\n\n• Access your personal data\n• Rectify incorrect information\n• Request data deletion\n• Object to data processing\n• Data portability\n• File a complaint with the data protection authority',
    
    'legal.terms.label': 'LEGAL',
    'legal.terms.title': 'Terms and Conditions',
    'legal.terms.lastUpdated': 'Last updated: January 2026',
    'legal.terms.section1.title': '1. Acceptance of Terms',
    'legal.terms.section1.content': 'By accessing and using Nexusdevtech services, you agree to be bound by these Terms and Conditions.\n\nIf you do not agree with any part of these terms, you must not use our services.',
    'legal.terms.section2.title': '2. Service Description',
    'legal.terms.section2.content': 'Nexusdevtech provides an operational security management platform that includes:\n\n• Real-time personnel tracking\n• Incident management and SOS alerts\n• Multi-user tactical communication\n• Reports and auditing\n• Integration with existing security systems',
    'legal.terms.section3.title': '3. Acceptable Use',
    'legal.terms.section3.content': 'You agree to use our services only for legitimate operational security purposes.\n\nProhibited:\n\n• Using the service for illegal activities\n• Attempting unauthorized access to systems\n• Interfering with service operation\n• Sharing access credentials\n• Reverse engineering the software',
    'legal.terms.section4.title': '4. Limitation of Liability',
    'legal.terms.section4.content': 'Nexusdevtech will not be liable for indirect, incidental, or consequential damages arising from the use of our services.\n\nOur liability is limited to the amount paid for the service in the last 12 months.',
    'legal.terms.section5.title': '5. Modifications',
    'legal.terms.section5.content': 'We reserve the right to modify these terms at any time. Modifications will take effect upon publication on our website.\n\nContinued use of the service after modifications constitutes your acceptance of the new terms.',
    
    'legal.security.label': 'SECURITY',
    'legal.security.title': 'Security and Data Protection',
    'legal.security.description': 'At Nexusdevtech, security is fundamental. We implement multiple layers of protection to ensure the integrity and confidentiality of your operational data.',
    'legal.security.encryption.title': 'Data Encryption',
    'legal.security.encryption.desc': 'All data is encrypted in transit (TLS 1.3) and at rest (AES-256). Sensitive communications use end-to-end encryption.',
    'legal.security.auth.title': 'Authentication',
    'legal.security.auth.desc': 'Mandatory multi-factor authentication (MFA), secure session management, and access tokens with automatic expiration.',
    'legal.security.monitoring.title': 'Continuous Monitoring',
    'legal.security.monitoring.desc': 'Intrusion detection system (IDS), behavior analysis, and automatic alerts for suspicious activities.',
    'legal.security.infrastructure.title': 'Secure Infrastructure',
    'legal.security.infrastructure.desc': 'Servers in certified data centers, automatic daily backups, and disaster recovery plans.',
    'legal.security.commitment.title': 'Our Commitment',
    'legal.security.commitment.content': 'We maintain the highest security standards in the industry.\n\nWe conduct regular security audits, penetration testing, and comply with international data protection regulations.\n\nIf you discover a security vulnerability, please contact us responsibly through our coordinated disclosure program.',
    
    // Contact Page
    'contact.label': 'CONTACT',
    'contact.title': 'Contact Us',
    'contact.description': 'Do you have questions about our services? We are here to help. Fill out the form and we will get back to you as soon as possible.',
    'contact.form.name': 'Name',
    'contact.form.name.placeholder': 'Your full name',
    'contact.form.email': 'Email',
    'contact.form.email.placeholder': 'your@email.com',
    'contact.form.company': 'Company',
    'contact.form.company.placeholder': 'Your company name (optional)',
    'contact.form.message': 'Message',
    'contact.form.message.placeholder': 'Tell us how we can help you...',
    'contact.form.submit': 'Send message',
    'contact.form.submitting': 'Sending...',
    'contact.success.title': 'Message sent!',
    'contact.success.message': 'Thank you for contacting us. We will respond shortly.',
    'contact.info.email.title': 'Email',
    'contact.info.response.title': 'Response time',
    'contact.info.response.desc': 'We respond within 24-48 business hours.',
  },
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('es')

  const t = (key: string): string => {
    const langTranslations = translations[language] as Record<string, string>
    return langTranslations[key] || key
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

